import { ExpandableContent } from "@nova/component-expandable-content"
import { ButtonPrimary, ButtonTertiary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { MissionChecklistSubmission, MissionFile } from "@nova/schemas"
import { Table } from "@nova/ds-component-table"
import { Status } from "@nova/ds-component-status"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useS3FileUrl } from "plugins/shared/s3url"
import { useS3File } from "themes/default-tailwind/panels/GlobalUploadContainer"
import { downloadUriSameOrigin } from "tools/browser/download"
import { useParams } from "react-router"
import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { v4 } from "uuid"
import {
    useCreateMissionFile,
    useDeleteMissionFile,
    useMissionFiles,
    useMissionChecklistSubmissions,
    missionFileS3Key,
} from "./MissionFileQueries"
import { useDownloadChecklistSubmission } from "features/pilot-tracking/checklists/ChecklistSubmissionQueries"
import { MissionFileUploadData, UploadMissionFileModal } from "./UploadMissionFileModal"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { formatBytes } from "@nova/format"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import classNames from "classnames"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { isFilenameForImage, isFilenameForPdf } from "@nova/util-strings"

type SortField = "name" | "type" | "size" | "dateUploaded" | "uploadedBy" | "expires"
type SortDir = "ASC" | "DESC"
type SortState = { field: SortField; dir: SortDir }

type FileTableRow =
    | { kind: "file"; data: MissionFile }
    | { kind: "checklist"; data: MissionChecklistSubmission }

export const MissionFilesCard = () => {
    const modal = useConfirmationModalContext()
    const { missionId } = useParams<{ missionId: string }>()
    const { isEditor } = useWorkspacePermissions()
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { data: files, isLoading } = useMissionFiles(missionId ?? "")
    const { data: checklistSubmissions, isLoading: isLoadingChecklists } =
        useMissionChecklistSubmissions(missionId ?? "")
    const { createMissionFile } = useCreateMissionFile(missionId ?? "")
    const members = useAppSelector(selectWorkspaceMembers)
    const [sort, setSort] = useState<SortState>({ field: "dateUploaded", dir: "DESC" })

    const handleSort = useCallback((field: SortField) => {
        setSort((prev) => {
            if (prev.field !== field) return { field, dir: "ASC" }
            return { field, dir: prev.dir === "ASC" ? "DESC" : "ASC" }
        })
    }, [])

    const sortedRows = useMemo<FileTableRow[]>(() => {
        const fileRows: FileTableRow[] = (files ?? []).map((f) => ({ kind: "file", data: f }))
        const checklistRows: FileTableRow[] = (checklistSubmissions ?? []).map((s) => ({
            kind: "checklist",
            data: s,
        }))
        const combined = [...fileRows, ...checklistRows]

        return combined.sort((a, b) => {
            const dir = sort.dir === "ASC" ? 1 : -1

            const getName = (row: FileTableRow) =>
                row.kind === "file" ? row.data.name : row.data.checklistName
            const getType = (row: FileTableRow) =>
                row.kind === "file" ? (row.data.fileName.split(".").pop() ?? "") : "Checklist"
            const getSize = (row: FileTableRow) =>
                row.kind === "file" ? (row.data.fileSizeBytes ?? 0) : 0
            const getDate = (row: FileTableRow) => new Date(row.data.createdAt).getTime()
            const getUploadedBy = (row: FileTableRow) => {
                const userId = row.kind === "file" ? row.data.createdBy : row.data.userId
                return members?.find((m) => m.userId === userId)?.fullName ?? ""
            }
            const getExpires = (row: FileTableRow) =>
                row.kind === "file" && row.data.expiresAt
                    ? new Date(row.data.expiresAt).getTime()
                    : null

            switch (sort.field) {
                case "name":
                    return getName(a).localeCompare(getName(b)) * dir
                case "type":
                    return getType(a).localeCompare(getType(b)) * dir
                case "size":
                    return (getSize(a) - getSize(b)) * dir
                case "dateUploaded":
                    return (getDate(a) - getDate(b)) * dir
                case "uploadedBy":
                    return getUploadedBy(a).localeCompare(getUploadedBy(b)) * dir
                case "expires": {
                    const aExp = getExpires(a)
                    const bExp = getExpires(b)
                    if (aExp === null && bExp === null) return 0
                    if (aExp === null) return 1
                    if (bExp === null) return -1
                    return (aExp - bExp) * dir
                }
            }
        })
    }, [files, checklistSubmissions, sort, members])

    const handleUpload = useCallback(() => {
        void modal.confirm<MissionFileUploadData>({
            RenderComponent: UploadMissionFileModal,
            asyncConfirmCallback: async ({ name, notes, expiresAt, file }) => {
                if (!orgId || !missionId) return

                const fileId = v4()
                await fileManagementApi.putFile(file, {
                    target: missionFileS3Key(orgId, missionId, fileId),
                })
                await createMissionFile({
                    id: fileId,
                    name,
                    notes: notes ?? null,
                    expires_at: expiresAt ?? null,
                    file_name: file.name,
                    file_size_bytes: file.size,
                })
            },
        })
    }, [modal, orgId, missionId, createMissionFile])

    const loading = isLoading || isLoadingChecklists
    const isEmpty = !loading && !sortedRows.length

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-white telegraf">Files</h2>
                {isEditor && (
                    <ButtonTertiary title="Upload" onClick={handleUpload} LeftIcon={Icons.Upload} />
                )}
            </div>
            <Table>
                <Table.Head>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("name")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Name</div>
                            <Table.SortIndicator
                                sortState={sort.field === "name" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("type")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Type</div>
                            <Table.SortIndicator
                                sortState={sort.field === "type" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("size")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Size</div>
                            <Table.SortIndicator
                                sortState={sort.field === "size" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("dateUploaded")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Date Uploaded</div>
                            <Table.SortIndicator
                                sortState={sort.field === "dateUploaded" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("uploadedBy")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Uploaded By</div>
                            <Table.SortIndicator
                                sortState={sort.field === "uploadedBy" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("expires")}
                    >
                        <div className="flex items-center gap-1">
                            <div>Expires</div>
                            <Table.SortIndicator
                                sortState={sort.field === "expires" ? sort.dir : "none"}
                            />
                        </div>
                    </Table.Header>
                    <Table.Header className="sticky right-0 w-px p-0" />
                </Table.Head>
                <Table.Body>
                    {loading && (
                        <Table.Row>
                            <Table.Cell className="text-gray-400">Loading...</Table.Cell>
                        </Table.Row>
                    )}
                    {isEmpty && (
                        <Table.Row>
                            <Table.Cell colSpan={6}>
                                <div className="flex flex-col items-center gap-1 py-8">
                                    <h3 className="text-white">No Files Uploaded</h3>
                                    <p className="text-gray-400 text-sm">
                                        Upload a file to add it to your flight.
                                    </p>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    )}
                    {sortedRows.map((row) =>
                        row.kind === "file" ? (
                            <MissionFileRow
                                key={row.data.id}
                                file={row.data}
                                missionId={missionId ?? ""}
                                uploadedBy={
                                    members?.find((m) => m.userId === row.data.createdBy)
                                        ?.fullName ?? "--"
                                }
                            />
                        ) : (
                            <ChecklistSubmissionRow
                                key={row.data.id}
                                submission={row.data}
                                uploadedBy={
                                    members?.find((m) => m.userId === row.data.userId)?.fullName ??
                                    "--"
                                }
                            />
                        )
                    )}
                </Table.Body>
            </Table>
        </div>
    )
}

const ChecklistSubmissionRow = ({
    submission,
    uploadedBy,
}: {
    submission: MissionChecklistSubmission
    uploadedBy: string
}) => {
    const { downloadChecklistSubmission, isDownloading } = useDownloadChecklistSubmission()
    const [showActions, setShowActions] = useState(false)

    const handleDownload = useCallback(() => {
        void downloadChecklistSubmission({
            checklistId: submission.checklistId,
            submissionId: submission.id,
        })
    }, [downloadChecklistSubmission, submission.checklistId, submission.id])

    return (
        <Table.Row
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            <Table.Cell>{submission.checklistName}</Table.Cell>
            <Table.Cell>Checklist</Table.Cell>
            <Table.Cell>--</Table.Cell>
            <Table.Cell>{new Date(submission.createdAt).toDateString()}</Table.Cell>
            <Table.Cell>{uploadedBy}</Table.Cell>
            <Table.Cell />
            <Table.Cell className="sticky right-0 w-px p-0">
                <div
                    className={classNames(
                        "absolute right-0 top-px bottom-px items-center gap-2 bg-gray-800 rounded-xl px-3 py-2 z-10",
                        { hidden: !showActions, flex: showActions }
                    )}
                >
                    <ButtonPrimary
                        title="Download"
                        onClick={handleDownload}
                        disabled={isDownloading}
                        LeftIcon={Icons.Download}
                    />
                </div>
            </Table.Cell>
        </Table.Row>
    )
}

const MissionFileRow = ({
    file,
    missionId,
    uploadedBy,
}: {
    file: MissionFile
    missionId: string
    uploadedBy: string
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const expandedRowRef = useRef<HTMLTableRowElement>(null)
    const { isEditor } = useWorkspacePermissions()
    const orgId = useAppSelector(selectActiveWorkspaceId())

    useEffect(() => {
        if (isOpen) expandedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }, [isOpen])
    const modal = useConfirmationModalContext()
    const { deleteMissionFile } = useDeleteMissionFile(missionId)

    const s3Key = orgId ? missionFileS3Key(orgId, missionId, file.id) : ""
    const { data: url } = useS3FileUrl(s3Key)

    const isPdf = isFilenameForPdf(file.fileName)

    const { data: fileBlob } = useS3File(s3Key, { disabled: !isOpen || !isPdf })

    const [displayUrl, setDisplayUrl] = useState<string>()
    const [showActions, setShowActions] = useState(false)
    useEffect(() => {
        if (!fileBlob) return
        const blob = new Blob([fileBlob], { type: "application/pdf" })
        const objectUrl = URL.createObjectURL(blob)
        setDisplayUrl(objectUrl)
        return () => URL.revokeObjectURL(objectUrl)
    }, [fileBlob])
    const canPreview = isPdf || isFilenameForImage(file.fileName)
    const isExpired = file.expiresAt ? new Date(file.expiresAt).getTime() < Date.now() : false

    const handleDownload = () => {
        if (!url) return
        void downloadUriSameOrigin(file.fileName, url)
    }

    const handleDelete = () => {
        void modal.confirm({
            RenderComponent: getGenericConfirmationModalComponent({
                title: "Delete File?",
                message: `Are you sure you want to delete ${file.name}?`,
                confirmText: "Delete File",
            }),
            asyncConfirmCallback: async () => {
                await deleteMissionFile(file.id)
            },
        })
    }

    return (
        <>
            <Table.Row
                className="cursor-pointer"
                onClick={() => setIsOpen((prev) => !prev)}
                onMouseEnter={() => setShowActions(true)}
                onMouseLeave={() => setShowActions(false)}
            >
                <Table.Cell>
                    <div className="h-full flex items-center gap-2">
                        <Icons.SmallIcon
                            Icon={isOpen ? Icons.ArrowDropdown : Icons.ArrowDropdownRight}
                        />
                        {file.name}
                    </div>
                </Table.Cell>
                <Table.Cell>{file.fileName.split(".").pop()?.toUpperCase() ?? "--"}</Table.Cell>
                <Table.Cell>
                    {file.fileSizeBytes != null ? formatBytes(file.fileSizeBytes) : "--"}
                </Table.Cell>
                <Table.Cell>{new Date(file.createdAt).toDateString()}</Table.Cell>
                <Table.Cell>{uploadedBy}</Table.Cell>
                <Table.Cell className="whitespace-nowrap w-px pr-1">
                    {file.expiresAt && (
                        <Status type={isExpired ? "error" : "positive"} variant="outline">
                            {new Date(file.expiresAt).toDateString()}
                        </Status>
                    )}
                </Table.Cell>
                <Table.Cell
                    onClick={(e) => e.stopPropagation()}
                    className="sticky right-0 w-px p-0"
                >
                    <div
                        className={classNames(
                            "absolute right-0 top-px bottom-px items-center gap-2 bg-gray-800 rounded-xl px-3 py-2 z-10",
                            { hidden: !showActions, flex: showActions }
                        )}
                    >
                        <ButtonPrimary
                            title="Download"
                            LeftIcon={Icons.Download}
                            onClick={handleDownload}
                        />
                        {isEditor && (
                            <IconButton onClick={handleDelete}>
                                <Icons.SmallIcon Icon={Icons.Trash} />
                            </IconButton>
                        )}
                    </div>
                </Table.Cell>
            </Table.Row>
            <Table.Row ref={expandedRowRef}>
                <Table.Cell className={classNames("!p-0", { "!border-b-0": !isOpen })} colSpan={3}>
                    <ExpandableContent isOpen={isOpen}>
                        <div className="flex items-start gap-6 px-10 py-4">
                            {canPreview && (
                                <div className="w-[300px] h-[150px] shrink-0 overflow-hidden">
                                    {isPdf ? (
                                        displayUrl ? (
                                            <embed
                                                src={`${displayUrl}#toolbar=0&navpanes=0`}
                                                type="application/pdf"
                                                className="w-full h-full"
                                            />
                                        ) : (
                                            <div className="w-full h-full animate-pulse bg-gray-700" />
                                        )
                                    ) : url ? (
                                        <img
                                            src={url}
                                            alt={file.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full animate-pulse bg-gray-700" />
                                    )}
                                </div>
                            )}
                            {file.notes && (
                                <h5 className="text-gray-100 max-w-[150px] whitespace-pre-wrap">
                                    {file.notes}
                                </h5>
                            )}
                        </div>
                    </ExpandableContent>
                </Table.Cell>
            </Table.Row>
        </>
    )
}
